'use client';

import { c as _c } from "react/compiler-runtime";
import type BN from 'bn.js';
import { useTranslations } from 'next-intl';
import { StatusBanner } from '@/components/ui/status-banner';
import { useFormattedCurrency } from '@/lib/currency';
import { useGiftFormStore } from '@/stores/gift-form';
interface SendGiftStep4Props {
  /** Display string for the recipient (e.g. "@cam_tutor"). Built by the orchestrator. */
  recipientDisplay: string;
  /**
   * USDC gift amount, or an outcome token's indicative marginal-spot value,
   * in D6. Outcome-token recipients receive tokens, not this USDC amount.
   */
  amountD6: BN;
  /** Platform fee (D6 BN). The live outcome-token send path passes zero. */
  feeD6: BN;
  /** True when sending will open a fresh vault cycle for this handle. */
  willInitNextCycle: boolean;
  /** Backend-quote loading state — disables the headline preview while pending. */
  quoteLoading: boolean;
}

/**
 * Step 4 — review the gift before signing.
 *
 * Pure presentational. Reads the form state from the store for handle/asset/
 * message/anonymous flags, and the orchestrator passes the exact cash amount
 * or the outcome token's indicative spot value.
 */
export function SendGiftStep4(props) {
  const $ = _c(74);
  const t = useTranslations("gift.send");
  const tReview = useTranslations("gift.send.steps.review");
  const {
    formatD6
  } = useFormattedCurrency();
  const platform = useGiftFormStore(_temp);
  const asset = useGiftFormStore(_temp2);
  const message = useGiftFormStore(_temp3);
  const anonymous = useGiftFormStore(_temp4);
  const selectedHolding = useGiftFormStore(_temp5);
  const isOutcomeToken = asset === "outcome_token";
  let assetLabel;
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== asset || $[1] !== formatD6 || $[2] !== isOutcomeToken || $[3] !== props.amountD6 || $[4] !== props.feeD6 || $[5] !== props.quoteLoading || $[6] !== props.recipientDisplay || $[7] !== props.willInitNextCycle || $[8] !== selectedHolding || $[9] !== t || $[10] !== tReview) {
    const totalD6 = props.amountD6.add(props.feeD6);
    let t4;
    if ($[16] !== asset || $[17] !== selectedHolding || $[18] !== tReview) {
      t4 = asset === "usdc" ? tReview("assetUsdc") : selectedHolding !== null ? `${tReview("assetOutcomeToken")} — ${selectedHolding.outcomeLabel ?? `Outcome ${selectedHolding.outcomeIndex}`}` : tReview("assetOutcomeToken");
      $[16] = asset;
      $[17] = selectedHolding;
      $[18] = tReview;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    assetLabel = t4;
    t3 = "flex flex-col gap-3";
    t0 = "flex flex-col gap-4 rounded-control border border-border-primary bg-background-primary p-4";
    let t5;
    if ($[20] !== isOutcomeToken || $[21] !== props.recipientDisplay || $[22] !== t) {
      t5 = isOutcomeToken ? t("asset.spotValueLabel") : t("fee.recipientReceives", {
        recipient: props.recipientDisplay
      });
      $[20] = isOutcomeToken;
      $[21] = props.recipientDisplay;
      $[22] = t;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    let t6;
    if ($[24] !== t5) {
      t6 = <p className="truncate text-caption text-foreground-secondary">{t5}</p>;
      $[24] = t5;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    let t7;
    if ($[26] !== formatD6 || $[27] !== props.amountD6) {
      t7 = formatD6(props.amountD6.toString(), "full");
      $[26] = formatD6;
      $[27] = props.amountD6;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    let t8;
    if ($[29] !== t7) {
      t8 = <p className="text-display font-semibold tabular-nums">{t7}</p>;
      $[29] = t7;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== isOutcomeToken || $[32] !== props.willInitNextCycle || $[33] !== t) {
      t9 = !isOutcomeToken && props.willInitNextCycle ? <p className="text-caption text-foreground-secondary">{t("fee.newCycleNote")}</p> : null;
      $[31] = isOutcomeToken;
      $[32] = props.willInitNextCycle;
      $[33] = t;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] !== props.quoteLoading || $[36] !== t) {
      t10 = props.quoteLoading ? <p className="text-caption text-foreground-secondary">{t("signing")}</p> : null;
      $[35] = props.quoteLoading;
      $[36] = t;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    if ($[38] !== t10 || $[39] !== t6 || $[40] !== t8 || $[41] !== t9) {
      t1 = <div className="flex flex-col gap-1">{t6}{t8}{t9}{t10}</div>;
      $[38] = t10;
      $[39] = t6;
      $[40] = t8;
      $[41] = t9;
      $[42] = t1;
    } else {
      t1 = $[42];
    }
    t2 = !isOutcomeToken ? <dl className="flex flex-col gap-1.5 border-t border-border-primary pt-3"><div className="flex justify-between text-caption text-foreground-secondary"><dt>{t("fee.label")}</dt><dd className="tabular-nums">{formatD6(props.feeD6.toString(), "full")}</dd></div><div className="flex justify-between text-body font-medium"><dt>{t("fee.total")}</dt><dd className="tabular-nums">{formatD6(totalD6.toString(), "full")}</dd></div></dl> : null;
    $[0] = asset;
    $[1] = formatD6;
    $[2] = isOutcomeToken;
    $[3] = props.amountD6;
    $[4] = props.feeD6;
    $[5] = props.quoteLoading;
    $[6] = props.recipientDisplay;
    $[7] = props.willInitNextCycle;
    $[8] = selectedHolding;
    $[9] = t;
    $[10] = tReview;
    $[11] = assetLabel;
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    assetLabel = $[11];
    t0 = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
  }
  let t4;
  if ($[43] !== tReview) {
    t4 = tReview("asset");
    $[43] = tReview;
    $[44] = t4;
  } else {
    t4 = $[44];
  }
  let t5;
  if ($[45] !== t4) {
    t5 = <dt className="text-caption text-foreground-secondary">{t4}</dt>;
    $[45] = t4;
    $[46] = t5;
  } else {
    t5 = $[46];
  }
  let t6;
  if ($[47] !== assetLabel) {
    t6 = <dd className="truncate text-caption">{assetLabel}</dd>;
    $[47] = assetLabel;
    $[48] = t6;
  } else {
    t6 = $[48];
  }
  let t7;
  if ($[49] !== t5 || $[50] !== t6) {
    t7 = <div className="flex items-start justify-between gap-3">{t5}{t6}</div>;
    $[49] = t5;
    $[50] = t6;
    $[51] = t7;
  } else {
    t7 = $[51];
  }
  let t8;
  if ($[52] !== anonymous || $[53] !== tReview) {
    t8 = anonymous ? <div className="flex items-start justify-between gap-3"><dt className="text-caption text-foreground-secondary">{tReview("anonymous")}</dt><dd className="text-caption">{tReview("anonymousYes")}</dd></div> : null;
    $[52] = anonymous;
    $[53] = tReview;
    $[54] = t8;
  } else {
    t8 = $[54];
  }
  let t9;
  if ($[55] !== message || $[56] !== tReview) {
    t9 = message.length > 0 ? <div className="flex flex-col gap-1"><dt className="text-caption text-foreground-secondary">{tReview("message")}</dt><dd className="rounded-item bg-background-tertiary/40 p-2 text-caption">{message}</dd></div> : null;
    $[55] = message;
    $[56] = tReview;
    $[57] = t9;
  } else {
    t9 = $[57];
  }
  let t10;
  if ($[58] !== t7 || $[59] !== t8 || $[60] !== t9) {
    t10 = <dl className="flex flex-col gap-2 border-t border-border-primary pt-3">{t7}{t8}{t9}</dl>;
    $[58] = t7;
    $[59] = t8;
    $[60] = t9;
    $[61] = t10;
  } else {
    t10 = $[61];
  }
  let t11;
  if ($[62] !== t0 || $[63] !== t1 || $[64] !== t10 || $[65] !== t2) {
    t11 = <div className={t0}>{t1}{t2}{t10}</div>;
    $[62] = t0;
    $[63] = t1;
    $[64] = t10;
    $[65] = t2;
    $[66] = t11;
  } else {
    t11 = $[66];
  }
  let t12;
  if ($[67] !== platform || $[68] !== tReview) {
    t12 = platform === "x" ? <StatusBanner tone="warning" title={tReview("xWarningTitle")} description={tReview("xWarningBody")} /> : null;
    $[67] = platform;
    $[68] = tReview;
    $[69] = t12;
  } else {
    t12 = $[69];
  }
  let t13;
  if ($[70] !== t11 || $[71] !== t12 || $[72] !== t3) {
    t13 = <div className={t3}>{t11}{t12}</div>;
    $[70] = t11;
    $[71] = t12;
    $[72] = t3;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  return t13;
}
function _temp5(s_3) {
  return s_3.selectedHolding;
}
function _temp4(s_2) {
  return s_2.anonymous;
}
function _temp3(s_1) {
  return s_1.message;
}
function _temp2(s_0) {
  return s_0.asset;
}
function _temp(s) {
  return s.platform;
}